/**
 * Day schedule engine — build a timeline and, crucially, REPAIR it after drift.
 *
 * The owner's recurring failure is a late start that scatters the whole day.
 * Instead of guilt, Life OS replans: fixed commitments (calendar events) stay
 * anchored; movable blocks (routines, scheduled tasks) that are now in the past
 * are pushed forward to start no earlier than "now", flowing around the fixed
 * blocks, until the day ends. Whatever cannot fit is surfaced as overflow to
 * defer — never silently dropped.
 *
 * Everything here is PURE and works in ISO-8601 instants.
 */

export type BlockKind = 'event' | 'routine' | 'task';

export interface TimelineBlock {
  readonly id: string;
  readonly title: string;
  readonly kind: BlockKind;
  readonly startsAt: string;
  readonly endsAt: string;
  /** Fixed blocks (events) keep their time; movable blocks can be replanned. */
  readonly movable: boolean;
}

const ms = (iso: string) => Date.parse(iso);
const iso = (n: number) => new Date(n).toISOString();
const durationMs = (b: TimelineBlock) => Math.max(0, ms(b.endsAt) - ms(b.startsAt));

/** Sort blocks chronologically by start, then by end. Pure. */
export function buildTimeline(blocks: readonly TimelineBlock[]): readonly TimelineBlock[] {
  return [...blocks].sort((a, b) => ms(a.startsAt) - ms(b.startsAt) || ms(a.endsAt) - ms(b.endsAt));
}

export interface RepairResult {
  /** Blocks that fit, in chronological order (fixed anchored, movable replanned). */
  readonly planned: readonly TimelineBlock[];
  /** Movable blocks that could not fit before day end — offer to defer. */
  readonly overflow: readonly TimelineBlock[];
  /** How many movable blocks were moved from their original start. */
  readonly shifted: number;
}

/**
 * Replan a day from `nowIso`. Fixed blocks stay; movable blocks are packed into
 * the gaps around them starting at `now`, preserving their relative order and
 * duration. Movable blocks that cannot fit before `dayEndIso` go to overflow.
 */
export function repairDay(
  blocks: readonly TimelineBlock[],
  nowIso: string,
  dayEndIso: string,
): RepairResult {
  const now = ms(nowIso);
  const dayEnd = ms(dayEndIso);

  const ordered = buildTimeline(blocks);
  const fixed = ordered.filter((b) => !b.movable);
  const movable = ordered.filter((b) => b.movable);

  // Fixed blocks that haven't fully passed remain as immovable anchors.
  const anchors = fixed
    .filter((b) => ms(b.endsAt) > now)
    .map((b) => ({ start: ms(b.startsAt), end: ms(b.endsAt) }))
    .sort((a, b) => a.start - b.start);

  const planned: TimelineBlock[] = fixed.filter((b) => ms(b.endsAt) > now);
  const overflow: TimelineBlock[] = [];
  let shifted = 0;
  let cursor = now;

  const fitsAt = (start: number, dur: number): number | null => {
    // Find the earliest slot >= start of length dur that avoids all anchors and the day end.
    let s = start;
    // Walk forward past any anchor the candidate would overlap.
    // Anchors are sorted; loop until stable.
    let moved = true;
    while (moved) {
      moved = false;
      for (const a of anchors) {
        if (s < a.end && s + dur > a.start) {
          s = a.end;
          moved = true;
        }
      }
    }
    return s + dur <= dayEnd ? s : null;
  };

  for (const b of movable) {
    const dur = durationMs(b);
    const slot = fitsAt(Math.max(cursor, now), dur);
    if (slot === null) {
      overflow.push(b);
      continue;
    }
    if (slot !== ms(b.startsAt)) shifted++;
    planned.push({ ...b, startsAt: iso(slot), endsAt: iso(slot + dur) });
    cursor = slot + dur;
  }

  return { planned: buildTimeline(planned), overflow, shifted };
}

export interface DayLoad {
  readonly fixedCount: number;
  readonly movableCount: number;
  readonly busyMinutes: number;
  readonly firstStart: string | null;
  readonly lastEnd: string | null;
}

/** Quick, calm summary of a day's load. Pure. */
export function summarizeDay(blocks: readonly TimelineBlock[]): DayLoad {
  const ordered = buildTimeline(blocks);
  const busy = ordered.reduce((a, b) => a + durationMs(b), 0);
  return {
    fixedCount: ordered.filter((b) => !b.movable).length,
    movableCount: ordered.filter((b) => b.movable).length,
    busyMinutes: Math.round(busy / 60000),
    firstStart: ordered.length ? ordered[0]!.startsAt : null,
    lastEnd: ordered.length ? ordered[ordered.length - 1]!.endsAt : null,
  };
}
